/**
 * Layer Panel — DOM overlay that displays toggleable layer visibility controls.
 *
 * Owns its own collapsed-category state. Fires callbacks when the user
 * toggles visibility; never touches PIXI or the layer data directly.
 */

export interface LayerPanelItem {
  id: string;
  visible: boolean;
  label?: string;
  category?: string;
  order: number;
}

export class LayerPanel {
  private panel: HTMLElement;
  private collapsed = new Map<string, boolean>();
  private onToggle: (id: string, visible: boolean) => void;
  private onCategoryToggle: (category: string, visible: boolean) => void;

  /** Snapshot used by rebuild — kept so event handlers can trigger a re-render. */
  private lastItems: LayerPanelItem[] = [];

  constructor(
    container: HTMLElement,
    callbacks: {
      onToggle: (id: string, visible: boolean) => void;
      onCategoryToggle: (category: string, visible: boolean) => void;
    },
  ) {
    this.onToggle = callbacks.onToggle;
    this.onCategoryToggle = callbacks.onCategoryToggle;

    const maxH = Math.max(
      100,
      Math.min(500, Math.round(container.clientHeight * 0.5)),
    );

    const panel = document.createElement("div");
    panel.style.cssText = `
      position: absolute;
      top: 8px;
      left: 8px;
      max-height: ${maxH}px;
      overflow-y: auto;
      background: rgba(10, 18, 32, 0.82);
      border: 1px solid #1a2a3a;
      border-radius: 4px;
      padding: 6px 0;
      font-family: monospace;
      font-size: 12px;
      color: #c0c8d0;
      z-index: 10;
      min-width: 120px;
      display: none;
      user-select: none;
    `;
    container.appendChild(panel);
    this.panel = panel;
  }

  /**
   * Rebuild the panel DOM from the given layer items.
   * Items should be pre-filtered (no builtin/hidden layers).
   */
  rebuild(items: LayerPanelItem[]): void {
    this.lastItems = items;
    this.panel.innerHTML = "";

    const sorted = [...items].sort((a, b) => a.order - b.order);

    if (sorted.length === 0) {
      this.panel.style.display = "none";
      return;
    }
    this.panel.style.display = "block";

    const seenCats = new Set<string>();

    for (const item of sorted) {
      if (item.category) {
        if (seenCats.has(item.category)) continue;
        seenCats.add(item.category);

        const catItems = sorted.filter((l) => l.category === item.category);
        const collapsed = this.collapsed.get(item.category) ?? false;

        const allVisible = catItems.every((l) => l.visible);
        const noneVisible = catItems.every((l) => !l.visible);

        // Category header
        const header = document.createElement("div");
        header.style.cssText = `
          padding: 6px 12px;
          cursor: pointer;
          color: #8a9ab0;
          font-weight: 600;
          font-size: 11px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 8px;
        `;

        // Category visibility toggle dot
        const catDotWrap = document.createElement("span");
        catDotWrap.style.cssText = `
          padding: 4px;
          margin: -4px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        `;
        const catDot = document.createElement("span");
        catDot.style.cssText = `
          width: 10px;
          height: 10px;
          border-radius: 50%;
          ${noneVisible
            ? "border: 1px solid #4a6a8a; background: none;"
            : allVisible
              ? "background: #50b0f0;"
              : "background: #50b0f0; opacity: 0.45;"}
        `;
        catDotWrap.appendChild(catDot);
        catDotWrap.addEventListener("click", (e) => {
          e.stopPropagation();
          this.onCategoryToggle(item.category!, !allVisible);
        });
        header.appendChild(catDotWrap);

        const headerLabel = document.createElement("span");
        headerLabel.textContent = `${collapsed ? "\u25B6" : "\u25BC"} ${item.category}`;
        headerLabel.style.cssText = "flex: 1;";
        headerLabel.addEventListener("click", () => {
          this.collapsed.set(item.category!, !collapsed);
          this.rebuild(this.lastItems);
        });
        header.appendChild(headerLabel);

        this.panel.appendChild(header);

        if (!collapsed) {
          for (const cl of catItems) {
            this.panel.appendChild(this.buildRow(cl, true));
          }
        }
      } else {
        this.panel.appendChild(this.buildRow(item, false));
      }
    }
  }

  private buildRow(item: LayerPanelItem, indented: boolean): HTMLElement {
    const row = document.createElement("div");
    row.style.cssText = `
      padding: 5px ${indented ? "24px" : "12px"};
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      opacity: ${item.visible ? "1" : "0.4"};
    `;
    row.addEventListener("mouseenter", () => {
      row.style.background = "rgba(255,255,255,0.05)";
    });
    row.addEventListener("mouseleave", () => {
      row.style.background = "none";
    });

    const dot = document.createElement("span");
    dot.style.cssText = `
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
      ${item.visible
        ? "background: #50b0f0;"
        : "border: 1px solid #4a6a8a; background: none;"}
    `;
    row.appendChild(dot);

    const label = document.createElement("span");
    label.textContent = item.label || item.id;
    label.style.cssText =
      "white-space: nowrap; overflow: hidden; text-overflow: ellipsis;";
    row.appendChild(label);

    row.addEventListener("click", () => {
      this.onToggle(item.id, !item.visible);
    });

    return row;
  }

  destroy(): void {
    this.panel.parentNode?.removeChild(this.panel);
  }
}
